import type { ComputedInterviewPayload } from '@Application/Beneficiaries/Interviews/Actions/interviewsFetched';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type PlainDate from '@Domain/PlainDate';
import InterviewConsentView from '@UI/Store/Interviews/Views/InterviewConsentView';
import RecordingView from '@UI/Store/Interviews/Views/RecordingView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';

export type InterviewResumeState = 'transfer-pending' | 'none';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `recording` and `consent` carry the full nested View, never a bare identifier: `resumeState`
 * branches on `consent.isWithdrawn` and `recording.completedAt`, fields an id alone could not carry.
 *
 * The constructor takes the raw fields rather than a payload so that `withoutRecording` — needed
 * when a consent withdrawal clears the audio while keeping the interview on screen — never has to
 * fabricate a fake `facility` to satisfy `ComputedInterviewPayload`. `fromPayload` is the one place
 * the wire shape is read.
 *
 * `kind` is never typed by the user — it is decided by which capture button was clicked — and this
 * View only ever reads it back, in `{ value, label }` form like every other enum.
 * `preparationId` is the most recent preparation containing the interview, resolved server-side; the
 * transcription is followed there rather than on the interview page.
 */
class InterviewView {
	readonly id: number;
	readonly clientReference: string | null;
	readonly name: string | null;
	readonly kind: LabeledValueView;
	readonly conductedOn: PlainDate | null;
	readonly participantContactIds: readonly number[];
	readonly participantStaffMemberIds: readonly number[];
	readonly recording: RecordingView | null;
	readonly consent: InterviewConsentView | null;
	readonly processingState: LabeledValueView;
	readonly preparationId: number | null;
	readonly transcriptionIssue: TranscriptionIssueView | null;

	constructor(
		id: number,
		clientReference: string | null,
		name: string | null,
		kind: LabeledValueView,
		conductedOn: PlainDate | null,
		participantContactIds: readonly number[],
		participantStaffMemberIds: readonly number[],
		recording: RecordingView | null,
		consent: InterviewConsentView | null,
		processingState: LabeledValueView,
		preparationId: number | null,
		transcriptionIssue: TranscriptionIssueView | null,
	) {
		this.id = id;
		this.clientReference = clientReference;
		this.name = name;
		this.kind = kind;
		this.conductedOn = conductedOn;
		this.participantContactIds = participantContactIds;
		this.participantStaffMemberIds = participantStaffMemberIds;
		this.recording = recording;
		this.consent = consent;
		this.processingState = processingState;
		this.preparationId = preparationId;
		this.transcriptionIssue = transcriptionIssue;

		Object.freeze(this);
	}

	static fromPayload(payload: ComputedInterviewPayload): InterviewView {
		return new InterviewView(
			payload.id,
			payload.clientReference,
			payload.name,
			new LabeledValueView(payload.kind),
			payload.conductedOn,
			payload.participantContacts.map((contact) => contact.id),
			payload.participantStaffMembers.map((staffMember) => staffMember.id),
			payload.recording !== null ? RecordingView.fromPayload(payload.recording, payload.id) : null,
			payload.consent !== null ? new InterviewConsentView(payload.consent, payload.id) : null,
			new LabeledValueView(payload.processingState),
			payload.preparation?.id ?? null,
			payload.transcriptionIssue !== null
				? new TranscriptionIssueView(payload.transcriptionIssue)
				: null,
		);
	}

	get recordingId(): number | null {
		return this.recording?.id ?? null;
	}

	/** The server's kind read as is; a value this client does not know counts as a conversation. */
	get knownKind(): InterviewKind {
		return (
			Object.values(InterviewKind).find((kind) => kind === this.kind.value) ??
			InterviewKind.CONVERSATION
		);
	}

	/** Whether the group's « notes » setting accepts this interview: a dictated note or an entered text. */
	get countsAsNote(): boolean {
		return this.kind.value === InterviewKind.NOTES || this.kind.value === InterviewKind.TEXT;
	}

	get consentId(): number | null {
		return this.consent?.id ?? null;
	}

	get isConsentWithdrawn(): boolean {
		return this.consent?.isWithdrawn === true;
	}

	/**
	 * Drives which action the folder screen offers next. Never recomputed in JSX: the safety rule
	 * that commands it — a capture screen must never reopen on a recording that already exists —
	 * lives here, in a getter, testable on its own. `source` is never a branching axis: only
	 * `completedAt` says whether work remains.
	 *
	 * Two values only: a server interview without a recording (a withdrawn consent leaves
	 * `recording === null`) has no resume path, so it always renders `'none'`.
	 */
	get resumeState(): InterviewResumeState {
		if (this.conductedOn === null) {
			return 'none';
		}

		if (this.isConsentWithdrawn) {
			return 'none';
		}

		if (this.recording === null) {
			return 'none';
		}

		return this.recording.completedAt === null ? 'transfer-pending' : 'none';
	}

	/**
	 * Clears `recording` and swaps in the consent the same server response carried: a withdrawal
	 * always changes both fields together, in one answer, so no intermediate state where `consent`
	 * is stale while `recording` is already cleared is ever representable.
	 */
	withoutRecording(consent: InterviewConsentView): InterviewView {
		return new InterviewView(
			this.id,
			this.clientReference,
			this.name,
			this.kind,
			this.conductedOn,
			this.participantContactIds,
			this.participantStaffMemberIds,
			null,
			consent,
			this.processingState,
			this.preparationId,
			this.transcriptionIssue,
		);
	}

	/** Replaces `recording` alone: the transfer pipeline learns a recording's progress and completion without ever touching the interview's own fields. */
	withRecording(recording: RecordingView): InterviewView {
		return new InterviewView(
			this.id,
			this.clientReference,
			this.name,
			this.kind,
			this.conductedOn,
			this.participantContactIds,
			this.participantStaffMemberIds,
			recording,
			this.consent,
			this.processingState,
			this.preparationId,
			this.transcriptionIssue,
		);
	}
}

export default InterviewView;
